import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, debounceTime, distinctUntilChanged, finalize, switchMap } from 'rxjs';
import { IOrderUser } from 'src/app/features/orders/models/order.model';
import { OrdersService } from 'src/app/features/orders/services/orders.services';
import { CommonService } from 'src/app/shared/common.service';

export interface ChangeOrderOwnerDialogData {
  orderId: number;
  currentOwner?: IOrderUser | null;
}

@Component({
  selector: 'app-change-order-owner-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
  templateUrl: './change-order-owner-dialog.component.html',
  styleUrls: ['./change-order-owner-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeOrderOwnerDialogComponent implements OnInit, OnDestroy {
  noteControl = new FormControl<string>('', { nonNullable: true });
  userSearchControl = new FormControl<string>('', { nonNullable: true });
  selectedUser: IOrderUser | null = null;
  options: IOrderUser[] = [];
  loadingOptions = false;
  saving = false;
  private search$ = new Subject<string>();
  private sub?: Subscription;

  constructor(
    private dialogRef: MatDialogRef<ChangeOrderOwnerDialogComponent, IOrderUser | undefined>,
    private ordersService: OrdersService,
    private commonService: CommonService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: ChangeOrderOwnerDialogData
  ) {}

  ngOnInit(): void {
    this.sub = this.search$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((q) => {
          this.loadingOptions = true;
          this.cdr.markForCheck();
          return this.ordersService.searchBackofficeUsers(q, 20).pipe(
            finalize(() => {
              this.loadingOptions = false;
              this.cdr.markForCheck();
            })
          );
        })
      )
      .subscribe((users) => {
        this.options = users;
        this.cdr.markForCheck();
      });
    this.search$.next('');
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onSearchInput(value: string): void {
    this.search$.next(value?.trim() ?? '');
  }

  onUserSelected(event: MatAutocompleteSelectedEvent): void {
    this.selectedUser = event.option.value as IOrderUser;
    this.userSearchControl.setValue(this.selectedUser.name, { emitEvent: false });
  }

  displayUser(u: IOrderUser | string | null): string {
    if (!u || typeof u === 'string') {
      return typeof u === 'string' ? u : '';
    }
    return u.name;
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  confirm(): void {
    if (!this.selectedUser?.id) {
      return;
    }
    this.saving = true;
    this.cdr.markForCheck();
    this.ordersService
      .updateOrderOwner(this.data.orderId, this.selectedUser.id, this.noteControl.value?.trim() || undefined)
      .pipe(finalize(() => { this.saving = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: (owner) => this.dialogRef.close(owner),
        error: () => {},
      });
  }
}
